import { LIST_TYPES, ListType } from 'constants/listTypes';
import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import { ListAction } from 'modules/campaigns/editor/types';
import { useCampaignContext, useEditorContext } from 'modules/campaigns/editor/context';
import { AudienceList } from 'modules/domains/campaign';
import { useEffect, useState } from 'react';
import { PaginationUpdateEvent } from 'ui/paginationFooter';
import { useDebouncedState } from 'utils/hooks/useDebouncedState';
import { getIsEligibilityCampaign } from 'modules/campaigns/editor/components/utils';
import { QueryAudienceListsSort, useQueryAudienceLists } from '../../../hooks';
import { ALL_LIST_TYPES } from '../components';
import { useListCreatorNames } from '../hooks';
import {
  AudienceContext,
  AudienceContextState,
  DEFAULT_LIST_TYPES,
  DEFAULT_SORT,
} from './AudienceContext';
import { useCalculateSelectedListsSizes } from '../hooks/useCalculateSelectedListsSizes';

const { DYNAMIC } = LIST_TYPES;

export type AudienceProviderProps = {
  children: React.ReactNode;
};

export const AudienceProvider = ({ children }: AudienceProviderProps) => {
  const { isAudienceTakeoverOpen } = useEditorContext();
  const { campaign } = useCampaignContext();
  const listCreatorNames = useListCreatorNames();
  const isEligibility = getIsEligibilityCampaign(campaign.campaignTypeDefinition);

  const [listTypes, setListTypes] = useState<ListType[]>(
    isEligibility ? [DYNAMIC] : DEFAULT_LIST_TYPES,
  );
  const [debouncedSearchText, searchText, setSearchText] = useDebouncedState('', 500);
  const [selectedSendLists, setSelectedSendLists] = useState<AudienceList[]>(campaign?.lists ?? []);
  const [selectedSuppressionLists, setSelectedSuppressionLists] = useState<AudienceList[]>(
    campaign?.suppressionLists ?? [],
  );

  const selectedListsCount = selectedSendLists.length + selectedSuppressionLists.length;

  const [sort, setSort] = useState<QueryAudienceListsSort>(DEFAULT_SORT);
  const [limit, setLimit] = useState<number>(10);
  const [offset, setOffset] = useState<number>(0);
  const [count, setCount] = useState<number>(0);
  const [page, setPage] = useState<number>(1);

  const selectedSendListsSizes = useCalculateSelectedListsSizes(selectedSendLists);
  const selectedSuppressionListsSizes = useCalculateSelectedListsSizes(selectedSuppressionLists);

  const listActionOpts = {
    [ListAction.Send]: {
      ids: selectedSendLists,
      update: setSelectedSendLists,
    },
    [ListAction.Suppress]: {
      ids: selectedSuppressionLists,
      update: setSelectedSuppressionLists,
    },
    [ListAction.Eligible]: {
      ids: selectedSendLists,
      update: setSelectedSendLists,
    },
  };

  const variables = {
    search: debouncedSearchText,
    sort,
    filterInfo: { listType: listTypes },
    pagination: {
      limit,
      offset,
    },
    isEmbedded: isEligibility,
  };

  const {
    info,
    results: lists,
    errorMessage,
    hasError,
    isLoading,
    refetch,
  } = useQueryAudienceLists(variables);

  useEffect(() => {
    const { lists: sendLists = [], suppressionLists = [] } = campaign;
    setSelectedSendLists(sendLists.map(({ id, name }) => ({ id, name })));
    setSelectedSuppressionLists(suppressionLists.map(({ id, name }) => ({ id, name })));
  }, [campaign, isAudienceTakeoverOpen]);

  useEffect(() => {
    setCount(info.count);
  }, [info?.count]);

  useEffect(() => {
    if (isEligibility) {
      setListTypes([DYNAMIC]);
    }
  }, [isEligibility]);

  const handlePageChange = ({ page: newPage, pageSize }: PaginationUpdateEvent) => {
    setLimit(pageSize);
    setOffset((newPage - 1) * pageSize);
    setPage(newPage);
  };

  const handleListTypeChange = (listType: string) => {
    setListTypes(listType === ALL_LIST_TYPES ? DEFAULT_LIST_TYPES : [listType as ListType]);
    setOffset(0);
    setPage(1);
  };

  const handleSort = (columnValue: string) => {
    const { sortBy, sortDirection } = sort;
    if (columnValue === sortBy) {
      const direction =
        sortDirection === SORT_DIRECTIONS.ASCENDING
          ? SORT_DIRECTIONS.DESCENDING
          : SORT_DIRECTIONS.ASCENDING;
      setSort({ ...sort, sortDirection: direction });
    } else {
      setSort({ sortBy: columnValue, sortDirection: SORT_DIRECTIONS.ASCENDING });
    }
  };

  const handleSearch = (text: string) => {
    setSearchText(text);
    setOffset(0);
    setPage(1);
  };

  const toggleListSelection = (listAction: ListAction, item: AudienceList) => {
    const selectedIds = listActionOpts[listAction].ids;
    const isAlreadySelected = selectedIds?.some((val: AudienceList) => val.id === item.id);

    if (listAction === ListAction.Eligible) {
      // Eligibility list is a radio input so just update to clicked item
      listActionOpts[listAction].update(isAlreadySelected ? [] : [item]);
    } else {
      const updatedSelections = isAlreadySelected
        ? selectedIds.filter((i) => i.id !== item.id)
        : [...selectedIds, item];

      listActionOpts[listAction].update(updatedSelections);
    }
  };

  const clearSelections = () => {
    setSelectedSendLists([]);
    setSelectedSuppressionLists([]);
  };

  const value: AudienceContextState = {
    lists,
    listCreatorNames,
    listTypes,
    selectedSendLists,
    selectedSuppressionLists,
    selectedListsCount,
    searchText,
    sort,
    pagination: { limit, offset, count, page },
    isLoading,
    hasError,
    errorMessage,
    selectedSendListsSizes,
    selectedSuppressionListsSizes,
    handlePageChange,
    handleListTypeChange,
    handleSort,
    handleSearch,
    toggleListSelection,
    refetch: () => refetch(variables),
    clearSelections,
  };

  return <AudienceContext.Provider value={value}>{children}</AudienceContext.Provider>;
};
